import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Film, Video, Check, RotateCcw, Search } from 'lucide-react';

const API = '/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const VouchersPanel = () => {
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all | unredeemed | redeemed
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vouchers`, { params: { status: filter } });
      setVouchers(r.data.vouchers || []);
      setStats(r.data.stats || null);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line
  useEffect(() => { load(); }, [filter]);

  const toggleRedeemed = async (voucher) => {
    const next = !voucher.redeemed;
    // Optimistic update
    setVouchers((v) => v.map((x) => (x.code === voucher.code ? { ...x, redeemed: next, redeemed_at: next ? new Date().toISOString() : null } : x)));
    try {
      await axios.post(`${API}/admin/vouchers/${voucher.code}/redeem`, { redeemed: next });
      toast.success(next ? `Marked ${voucher.code} redeemed` : `Reset ${voucher.code}`);
      // Refresh stats
      load();
    } catch (e) {
      toast.error('Failed to update voucher');
      // Revert
      setVouchers((v) => v.map((x) => (x.code === voucher.code ? { ...x, redeemed: voucher.redeemed, redeemed_at: voucher.redeemed_at } : x)));
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return vouchers;
    const q = search.trim().toLowerCase();
    return vouchers.filter((v) =>
      (v.code || '').toLowerCase().includes(q)
      || (v.owner_email || '').toLowerCase().includes(q)
      || (v.owner_name || '').toLowerCase().includes(q)
      || (v.booking_number || '').toLowerCase().includes(q)
    );
  }, [vouchers, search]);

  return (
    <div
      className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 backdrop-blur-sm border border-purple-500/20 rounded-xl p-3 sm:p-6"
      data-testid="vouchers-panel"
    >
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">Reel / Music Video Vouchers</h2>
          <p className="text-xs sm:text-sm text-gray-400">
            Track and redeem August bonus vouchers. One click to mark redeemed at session time.
          </p>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
          <div className="bg-fuchsia-900/20 border border-fuchsia-500/30 rounded-lg p-3">
            <div className="text-[10px] text-fuchsia-300 uppercase tracking-wide flex items-center gap-1">
              <Film className="w-3 h-3" /> Reels Issued
            </div>
            <div className="text-2xl font-bold text-white" data-testid="stat-total-reels">{stats.total_reel}</div>
          </div>
          <div className="bg-pink-900/20 border border-pink-500/30 rounded-lg p-3">
            <div className="text-[10px] text-pink-300 uppercase tracking-wide flex items-center gap-1">
              <Video className="w-3 h-3" /> Music Videos
            </div>
            <div className="text-2xl font-bold text-white" data-testid="stat-total-mv">{stats.total_music_video}</div>
          </div>
          <div className="bg-emerald-900/20 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-[10px] text-emerald-300 uppercase tracking-wide">Redeemed</div>
            <div className="text-2xl font-bold text-white" data-testid="stat-redeemed">{stats.redeemed}</div>
          </div>
          <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3">
            <div className="text-[10px] text-amber-300 uppercase tracking-wide">Outstanding</div>
            <div className="text-2xl font-bold text-white" data-testid="stat-outstanding">{stats.outstanding ?? stats.unredeemed}</div>
          </div>
          <div className="bg-gray-800/40 border border-gray-500/30 rounded-lg p-3">
            <div className="text-[10px] text-gray-300 uppercase tracking-wide">Expired</div>
            <div className="text-2xl font-bold text-white" data-testid="stat-expired">{stats.expired ?? 0}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="flex flex-wrap gap-1 bg-purple-900/20 border border-purple-500/20 rounded-lg p-1">
          {[
            { id: 'all', label: 'All' },
            { id: 'outstanding', label: 'Outstanding' },
            { id: 'redeemed', label: 'Redeemed' },
            { id: 'expired', label: 'Expired' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`voucher-filter-${f.id}`}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition ${
                filter === f.id
                  ? 'bg-purple-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-purple-800/40'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex-1 relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, name, email, booking #…"
            data-testid="voucher-search"
            className="w-full pl-9 pr-3 py-2 bg-purple-900/20 border border-purple-500/20 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-purple-400"
          />
        </div>
        <button
          onClick={load}
          data-testid="voucher-refresh"
          className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded-lg transition"
        >
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto -mx-3 sm:mx-0">
        <div className="inline-block min-w-full align-middle">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-purple-500/20 text-left">
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs">Type</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs">Code</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs">Client</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs hidden md:table-cell">Booking</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs hidden lg:table-cell">Hours</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs hidden md:table-cell">Issued</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs">Status</th>
                <th className="py-2 px-2 text-gray-400 font-semibold text-xs text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-6 text-center text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500">
                    No vouchers {filter !== 'all' ? `(${filter})` : ''} yet.
                  </td>
                </tr>
              ) : filtered.map((v) => {
                const isMV = v.type === 'music_video';
                return (
                  <tr
                    key={v.code}
                    className="border-b border-purple-500/10 hover:bg-purple-500/5"
                    data-testid={`voucher-row-${v.code}`}
                  >
                    <td className="py-2 px-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${
                        isMV ? 'bg-pink-500/20 text-pink-300 border border-pink-500/40' : 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40'
                      }`}>
                        {isMV ? <Video className="w-3 h-3" /> : <Film className="w-3 h-3" />}
                        {isMV ? 'MV' : 'Reel'}
                      </span>
                    </td>
                    <td className="py-2 px-2 font-mono font-bold text-white text-xs sm:text-sm">{v.code}</td>
                    <td className="py-2 px-2 text-gray-300">
                      <div className="text-xs sm:text-sm">{v.owner_name || '—'}</div>
                      <div className="text-[10px] text-gray-500">{v.owner_email}</div>
                    </td>
                    <td className="py-2 px-2 text-gray-300 hidden md:table-cell text-xs">{v.booking_number || '—'}</td>
                    <td className="py-2 px-2 text-gray-300 hidden lg:table-cell text-xs">{v.booking_hours || '—'} hr</td>
                    <td className="py-2 px-2 text-gray-400 hidden md:table-cell text-xs">{formatDate(v.created_at)}</td>
                    <td className="py-2 px-2">
                      {v.redeemed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                          <Check className="w-3 h-3" /> REDEEMED
                        </span>
                      ) : v.expired ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-500/20 text-gray-300 border border-gray-500/40">
                          EXPIRED
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                          OUTSTANDING
                        </span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <button
                        onClick={() => toggleRedeemed(v)}
                        data-testid={`voucher-toggle-${v.code}`}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                          v.redeemed
                            ? 'bg-gray-700/50 hover:bg-gray-700 text-gray-300 border border-gray-600'
                            : v.expired
                            ? 'bg-purple-700/60 hover:bg-purple-700 text-white border border-purple-500/50'
                            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        }`}
                        title={v.expired && !v.redeemed ? 'Honor this expired voucher and mark redeemed' : ''}
                      >
                        {v.redeemed ? (
                          <>
                            <RotateCcw className="w-3 h-3" /> Undo
                          </>
                        ) : v.expired ? (
                          <>
                            <Check className="w-3 h-3" /> Honor & Redeem
                          </>
                        ) : (
                          <>
                            <Check className="w-3 h-3" /> Mark Redeemed
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VouchersPanel;
